import { Country } from '@afterpay/types'
import { Route } from '../../config/router'
import { PaymentOption, paymentOptionsErrors, TerminalErrorCode } from '../../model'
import {
  setCheckoutError,
  setPaymentOptionsError,
  setShowEfaModal,
  setShowPaymentOptionsErrorModal,
  useAppDispatch,
  useAppRouter,
  useCountry,
  usePBIRejected,
  usePaymentOptionsError,
  useSelectedPaymentOption,
} from '../../state'
import { useState } from 'react'
import { FetchVerificationErrorResponse } from '@afterpaytouch/event-verify'
import { ErrorCode } from '@afterpaytouch/portal-api'

interface IUseHandlePaymentOptionsError {
  handlePaymentOptionsError: (e: any) => void
}

interface IUseSendEFARobocallEvent {
  onSuccess: () => void
  onError: (d: FetchVerificationErrorResponse) => void
  isSuccess: boolean
  isDecline: boolean
}

export const useHandlePaymentOptionsError = (): IUseHandlePaymentOptionsError => {
  const dispatch = useAppDispatch()
  const isPBIRejected = usePBIRejected()
  const router = useAppRouter()
  const country = useCountry()
  const currentPaymentOptionsError = usePaymentOptionsError()

  const handlePaymentOptionsError = (e): void => {
    const errorKey = paymentOptionsErrors[e.errorCode] ?? TerminalErrorCode.ErrorPCL
    const subReason = errorKey === TerminalErrorCode.RiskDeclinedPCL ? e.errorInfo?.sub_reason : undefined
    const subErrorKey = subReason === 'credit_freeze' ? TerminalErrorCode.CreditFreezeDeclinedPCL : errorKey
    const maximumOrderValue = errorKey === TerminalErrorCode.AmountTooHighDeclinePCL ? e.errorInfo?.maximum_order_value : undefined

    if (errorKey === TerminalErrorCode.ExtendedFraudAlert) {
      dispatch(setShowEfaModal(true))

      dispatch(
        setPaymentOptionsError({
          error: subErrorKey,
          errorCode: e.errorCode,
        })
      )
      return
    }

    if (e.errorCode === ErrorCode.TooManyRequests && currentPaymentOptionsError?.error === TerminalErrorCode.CreditFreezeDeclinedPCL) {
      // Ensure that the most recent error was credit freeze before redirecting them back to the credit freeze decline page.
      dispatch(
        setPaymentOptionsError({
          ...currentPaymentOptionsError,
          extra: {
            ...currentPaymentOptionsError.extra,
            tooManyRequests: true,
          },
        })
      )
      router.push(Route.RETRY_CREDIT_FREEZE)
      return
    }

    if (subErrorKey === TerminalErrorCode.CreditFreezeDeclinedPCL && e.errorInfo?.credit_freeze_application_deadline) {
      const creditFreezeApplicationDeadline = e.errorInfo.credit_freeze_application_deadline
      dispatch(
        setPaymentOptionsError({
          error: subErrorKey,
          errorCode: e.errorCode,
          ...{ extra: { creditFreezeApplicationDeadline, tooManyRequests: false } },
        })
      )
      router.push(Route.RETRY_CREDIT_FREEZE)
      return
    }

    if (isPBIRejected) {
      dispatch(
        setCheckoutError({
          error: subErrorKey,
          errorCode: e.errorCode,
          extra: {
            maximumOrderValue,
          },
        })
      )

      router.push(Route.TERMINAL_ERROR)
    } else {
      // show paymentOptions modal in US only?
      if (country !== Country.AU) {
        dispatch(setShowPaymentOptionsErrorModal(true))
      }
      dispatch(
        setPaymentOptionsError({
          error: subErrorKey,
          errorCode: e.errorCode,
          ...(maximumOrderValue !== undefined && { extra: { maximumOrderValue } }),
        })
      )
    }
  }
  return { handlePaymentOptionsError }
}

export const useSendEFARobocallEvent = (): IUseSendEFARobocallEvent => {
  const [efaRobocallSuccess, setEfaRobocallSuccess] = useState(false)
  const [efaRobocallDecline, setEfaRobocallDecline] = useState(false)

  const onSuccess = (): void => {
    setEfaRobocallSuccess(true)
    setEfaRobocallDecline(false)
  }
  const onError = (e: FetchVerificationErrorResponse): void => {
    setEfaRobocallSuccess(false)
    const error = e.message?.toLowerCase()
    // if it is fraud in efa flow, backend returns "Declined" as error data
    if (error === 'declined') {
      setEfaRobocallDecline(true)
    }
  }

  const isSuccess = efaRobocallSuccess ?? false
  const isDecline = efaRobocallDecline ?? false
  return {
    onSuccess,
    onError,
    isSuccess,
    isDecline,
  }
}

// This function is used to store the selected payment option of the UI state.
export const useSelectedPaymentOptionState = (): [PaymentOption | undefined, (paymentOption: PaymentOption) => void] => {
  const selectedPaymentOption = useSelectedPaymentOption()
  const [selectedPaymentOptionLocal, setSelectedPaymentOptionLocal] = useState(selectedPaymentOption)

  return [selectedPaymentOptionLocal, setSelectedPaymentOptionLocal]
}
